import { Component, inject, signal } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ChangePasswordService } from '@artemis-software/wr-api';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import * as Sentry from '@sentry/angular';
import { AuthService } from '@services/auth.service';

@Component({
  standalone: false,
  selector: 'wr-force-password-change-page',
  templateUrl: './force-password-change-page.component.html',
  styleUrls: ['./force-password-change-page.component.scss'],
})
export class ForcePasswordChangePageComponent {
  private readonly changePasswordService = inject(ChangePasswordService);
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);

  readonly submitting = signal(false);
  readonly serverError = signal('');

  // Kein Feld für das temporäre Passwort: der Login unmittelbar davor hat es bereits geprüft, das
  // Backend verlangt es für den Zwangswechsel deshalb nicht mehr.
  form = new FormGroup({
    newPassword: new FormControl('', [Validators.required, Validators.minLength(8)]),
    repeatPassword: new FormControl('', [Validators.required]),
  });

  async submit(): Promise<void> {
    if (this.form.invalid) return;
    if (this.form.value.newPassword !== this.form.value.repeatPassword) {
      this.serverError.set('Passwörter stimmen nicht überein.');
      return;
    }
    this.submitting.set(true);
    this.serverError.set('');
    try {
      const result = await firstValueFrom(this.changePasswordService.changeOwnPassword({
        newPassword: this.form.value.newPassword ?? '',
        repeatPassword: this.form.value.repeatPassword ?? '',
      }));
      if (!result.success) {
        this.serverError.set(result.message ?? 'Ein Fehler ist aufgetreten.');
        return;
      }
      // Ohne Anschluss-Token wäre das Token im Browser widerrufen und jeder Klick danach ein 401 -
      // dann lieber sauber auf die Anmeldung als mit einer toten Sitzung weiterlaufen.
      if (!result.token) {
        this.hardLogout();
        return;
      }
      this.auth.adoptRotatedToken(result.token);
      await this.router.navigate(['/landing']);
    } catch {
      this.serverError.set('Ein Fehler ist aufgetreten. Bitte erneut versuchen.');
    } finally {
      this.submitting.set(false);
    }
  }

  async logout(): Promise<void> {
    await this.auth.logout();
  }

  private hardLogout(): void {
    localStorage.removeItem('wr-jwt');
    Sentry.setUser(null);
    window.location.href = '/login';
  }
}
